import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuditLogEntry } from '@utils/auditLog.ts';
import { loadAuditLogs } from '@core/actions/auditLogActions.ts';
import { FetchStatusType } from '@core/modeles/model.ts';
import { RootState } from '@core/store/store.ts';
import { PaginateResponse } from '@utils/appUtils.ts';

interface AuditLogState {
  logs: PaginateResponse<AuditLogEntry> | null;
  loading: FetchStatusType;
  filterAction: string;
  filterUsername: string;
  filterFrom: string;
  filterTo: string;
  page: number;
  size: number;
}

const initialState: AuditLogState = {
  logs: null,
  loading: FetchStatusType.IDLE,
  filterAction: '',
  filterUsername: '',
  filterFrom: '',
  filterTo: '',
  page: 0,
  size: 10,
};

const AuditLogSlice = createSlice({
  name: 'auditLog',
  initialState,
  reducers: {
    setAuditPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setAuditSize(state, action: PayloadAction<number>) {
      state.size = action.payload;
      state.page = 0;
    },
    setAuditFilterAction(state, action: PayloadAction<string>) {
      state.filterAction = action.payload;
      state.page = 0;
    },
    setAuditFilterUsername(state, action: PayloadAction<string>) {
      state.filterUsername = action.payload;
      state.page = 0;
    },
    setAuditFilterFrom(state, action: PayloadAction<string>) {
      state.filterFrom = action.payload;
      state.page = 0;
    },
    setAuditFilterTo(state, action: PayloadAction<string>) {
      state.filterTo = action.payload;
      state.page = 0;
    },
    resetAuditFilters(state) {
      state.filterAction = '';
      state.filterUsername = '';
      state.filterFrom = '';
      state.filterTo = '';
      state.page = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAuditLogs.pending, (state) => {
        state.loading = FetchStatusType.LOADING;
      })
      .addCase(loadAuditLogs.fulfilled, (state, action) => {
        state.loading = FetchStatusType.COMPLETED;
        state.logs = action.payload;
      })
      .addCase(loadAuditLogs.rejected, (state) => {
        state.loading = FetchStatusType.FAILED;
      });
  },
});

export const {
  setAuditPage,
  setAuditSize,
  setAuditFilterAction,
  setAuditFilterUsername,
  setAuditFilterFrom,
  setAuditFilterTo,
  resetAuditFilters,
} = AuditLogSlice.actions;

// Sélecteurs
export const auditLogs = (state: RootState) => state.auditLog.logs;
export const auditLoading = (state: RootState) => state.auditLog.loading;
export const auditPage = (state: RootState) => state.auditLog.page;
export const auditSize = (state: RootState) => state.auditLog.size;
export const auditTotal = (state: RootState) => state.auditLog.logs?.total ?? 0;
export const auditFilterAction = (state: RootState) => state.auditLog.filterAction;
export const auditFilterUsername = (state: RootState) => state.auditLog.filterUsername;
export const auditFilterFrom = (state: RootState) => state.auditLog.filterFrom;
export const auditFilterTo = (state: RootState) => state.auditLog.filterTo;

export default AuditLogSlice.reducer;
